import React from 'react';
import { Card, List, Button, Badge, Spin, Empty } from 'antd';
import { ClockCircleOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { useGetGuarenteedLoans } from '@/shared/api/client/loans';
import { formatCurrency } from '@/shared/utils/formatters';

/**
 * Dashboard component that displays pending guarantor requests
 */
const PendingGuarantorRequests: React.FC = () => {
  const navigate = useNavigate();
  const authUser = useAuthUser();

  // Fetch guaranteed loans
  const { data: guaranteedLoans, isLoading } = useGetGuarenteedLoans(
    authUser.branch || '', 
    authUser.custNo || '',
    //@ts-ignore
    {},
    {
      query: { 
        enabled: !!authUser?.custNo && !!authUser?.branch,
        staleTime: 5 * 60 * 1000 // 5 minutes cache
      } 
    }
  );

  // Filter for pending guarantor decisions
  const pendingGuarantees = guaranteedLoans?.data?.filter(
    loan => loan.guarantorApprove === undefined || loan.guarantorApprove === null
  ) || [];

  // Navigate to guarantor requests page
  const viewAllRequests = () => {
    navigate('/loans/guarantor-requests');
  };

  // Navigate to specific loan guarantee detail
  const viewGuaranteeDetail = (loanId: string) => {
    navigate(`/action-flow/${loanId}`);
  };

  if (isLoading) {
    return (
      <Card title="Pending Guarantor Requests" className="h-full">
        <div className="flex justify-center items-center h-40">
          <Spin />
        </div>
      </Card>
    );
  }

  return (
    <Card 
      title={
        <div className="flex items-center">
          <span>Pending Guarantor Requests</span>
          {pendingGuarantees.length > 0 && (
            <Badge 
              count={pendingGuarantees.length} 
              style={{ marginLeft: 8, backgroundColor: '#faad14' }} 
            />
          )}
        </div>
      }
      extra={
        <Button type="link" onClick={viewAllRequests}>
          View All
        </Button>
      }
      className="h-full"
    >
      {pendingGuarantees.length > 0 ? (
        <List
          dataSource={pendingGuarantees.slice(0, 5)} // Show maximum 5 items
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button 
                  type="primary" 
                  size="small" 
                  icon={<CheckOutlined />}
                  onClick={() => viewGuaranteeDetail(item.sourceId || '')}
                >
                  Review
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={<ClockCircleOutlined style={{ fontSize: 24, color: '#faad14' }} />}
                title={`${item.name || 'Applicant'} - ${item.productName || 'Loan'}`}
                description={
                  <div>
                    <div>Amount: {formatCurrency(item.loanAmount || 0)}</div>
                    <div>Period: {item.loanPeriod} months</div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
          description="No pending guarantor requests"
        />
      )}
    </Card>
  );
};

export default PendingGuarantorRequests;
